import { Card, Collapse, Collection, Link } from '@ksyos/design-system';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { ActiveOrInactive } from '@common/enums';
import type { EpisodeCategory } from '../../../../../common/enums/episodeCategory';
import type { IEmploymentCarePaths } from './PersonalSettingsCarePathsOverview';
import { translateKey } from '../../../../translate';
import { EmptyListPlaceholder } from '../../../elements/EmptyListPlaceholder';
import { PersonalSettingsCarePathDetailsCollectionLinkItem } from './PersonalSettingsCarePathDetailsCollectionLinkItem';

interface IPersonalSettingsDetailsProps {
    activeEmploymentCarePaths: IEmploymentCarePaths;
    inActiveEmploymentCarePaths: IEmploymentCarePaths;
    pathname: string;
}

export function PersonalSettingsCarePathDetails(props: IPersonalSettingsDetailsProps) {
    const [showInactivePaths, setShowInactivePaths] = useState(false);

    if (isEmpty(props.activeEmploymentCarePaths) && isEmpty(props.inActiveEmploymentCarePaths)) {
        return <EmptyListPlaceholder subtitle={translateKey('practitioner.noActiveCarePaths')} />;
    }

    if (isEmpty(props.inActiveEmploymentCarePaths)) {
        return (
            <Card>
                <Collection type="ul">
                    {Object.keys(props.activeEmploymentCarePaths).map(renderActiveLinkItems)}
                </Collection>
            </Card>
        );
    }

    if (isEmpty(props.activeEmploymentCarePaths)) {
        return (
            <Card>
                <Collection type="ul">
                    {Object.keys(props.inActiveEmploymentCarePaths).map(renderInactiveLinkItems)}
                </Collection>
            </Card>
        );
    }

    return (
        <Card>
            <Collection
                aria-label={translateKey('episodeCategory')}
                type="ul"
            >
                {Object.keys(props.activeEmploymentCarePaths).map(renderActiveLinkItems)}
                <Collapse isOpen={showInactivePaths}>
                    {Object.keys(props.inActiveEmploymentCarePaths).map(renderInactiveLinkItems)}
                </Collapse>
            </Collection>
            <Card.Footer>
                <Link
                    color="minimal"
                    fontWeight="normal"
                    iconLeft={showInactivePaths ? 'caret-up' : 'caret-down'}
                    onClick={toggleShowInactiveCarePaths}
                    size="sm"
                    variant="button"
                >
                    {showInactivePaths
                        ? translateKey('episodeCategory.foldInactiveEpisodeCategory')
                        : translateKey('episodeCategory.showInactiveEpisodeCategory')}
                </Link>
            </Card.Footer>
        </Card>
    );

    function renderActiveLinkItems(episodeCategory: string) {
        return renderLinkItems(episodeCategory, true);
    }

    function renderInactiveLinkItems(episodeCategory: string) {
        return renderLinkItems(episodeCategory, false);
    }

    function renderLinkItems(episodeCategory: string, isActive: boolean) {
        const carePaths = isActive ? props.activeEmploymentCarePaths : props.inActiveEmploymentCarePaths;
        const serviceType = isActive ? ActiveOrInactive.active : ActiveOrInactive.inactive;
        const subtitle = getServiceGroups(carePaths, episodeCategory, serviceType).join(', ');
        const id = `${episodeCategory}-${Date.now()}`;

        return (
            <PersonalSettingsCarePathDetailsCollectionLinkItem
                isActive={isActive}
                key={id}
                pathname={props.pathname}
                subtitle={subtitle}
                title={episodeCategory as EpisodeCategory}
            />
        );
    }

    function getServiceGroups(carePaths: IEmploymentCarePaths, episodeCategory: string, serviceType: ActiveOrInactive) {
        const serviceGroups: string[] = [];

        for (const serviceGroup of Object.entries(carePaths[episodeCategory][serviceType])) {
            const name = serviceGroup[0];
            const serviceGroupRights = serviceGroup[1];

            if (serviceGroupRights.every((right) => right.rightType !== 'serviceReader')) {
                serviceGroups.push(name);
            }
        }

        return serviceGroups;
    }

    function toggleShowInactiveCarePaths() {
        setShowInactivePaths(!showInactivePaths);
    }
}
